// Package Imports
import React, { useEffect, useState, useLayoutEffect } from "react";
import { message, Input, Tooltip } from "antd";
import { Link, useNavigate } from "react-router-dom";

// Internal Imports
import { HeadingOne, BodyOne } from "../components/typography";
import BlueButton from "../components/buttons/BlueButton";
import AuthForm from "../components/forms/AuthForm";
import AuthPageShell from "../components/forms/AuthPageShell";
import { useLoginMutation } from "../hooks";
import SupportBlock from "../components/info/SupportBlock";
import mixpanelService from "../services/mixpanel";
import { useSSOData } from "../hooks";
import bugfenderHelper from "../services/bugfender";
import { question_mark_icon } from "../assets/icons";

const Login = () => {
  const navigate = useNavigate();
  const [ loginLoading, setLoginLoading ] = useState(false);
  const { validSSOData } = useSSOData();

  const [loginForm, setLoginForm] = useState({
    username: "",
    password: ""
  });

  const { loading, verificationCodeLoading, handleLogin } = useLoginMutation({
    variable: loginForm,
    ssoData: validSSOData,
    onCompletedCallback: () => {
      navigate(`/verification-step-one`);
    }
  });

  const navigateForgotPassword = () => {
    navigate("/forgot-password", {
      state: {
        isResetPassword: false
      }
    });
    mixpanelService.track('App.Forgot.Password', {
      username: loginForm.username,
    });
  }

  const handleChange = (e, field) => {
    const formState = { ...loginForm };

    formState[field] =
      field === "password"
        // ? base64Encoder(e.currentTarget.value)
        ? e.currentTarget.value
        : e.currentTarget.value;
    setLoginForm(formState);
  }

  const handlerLogin = async () => {
    if (loginForm.username.length <= 8 && loginForm.password.length === 0) {
      bugfenderHelper.error('Invalid email address or password.')
      message.error('Please enter a valid email address or password.');
      return
    }

    mixpanelService.people.set({
      username: loginForm.username,
    });

    handleLogin();
  }

  // SIDE-EFFECT - Login Loading State
  useEffect(() => {
    if (loading || verificationCodeLoading) {
      setLoginLoading(true);
    }

    if (!loading && !verificationCodeLoading) {
      setLoginLoading(false);
    }
  }, [loading, verificationCodeLoading]);

  return (
    <AuthPageShell>
          <AuthForm>
            <HeadingOne className="text-[#003eb2] text-center text-2xl font-semibold mb-6">
              Login
            </HeadingOne>
            <div className="flex flex-col">
              <label htmlFor="emailAddressLogin" className="flex items-center justify-between mb-1.5">
                <BodyOne className="mb-0 text-slate-600 font-medium">Email or mobile number</BodyOne>
                <Tooltip title={
                  <div>
                    <p>If you signed up with your email address, please use it to sign in.</p>
                    <br />
                    <p>Likewise, if you used your cellphone number to create an account, please use it to sign in.</p>
                  </div>
                }>
                  {question_mark_icon()}
                </Tooltip>
              </label>
              <Input
                id="emailAddressLogin"
                size="large"
                placeholder="someone@email.com or +27984895844"
                onChange={(e) => handleChange(e, "username")}
              />

              <label htmlFor="passwordLogin" className="mt-4 mb-1.5">
                <BodyOne className="mb-0 text-slate-600 font-medium">Password</BodyOne>
              </label>
              <Input.Password
                id="passwordLogin"
                size="large"
                placeholder="Enter your password"
                onChange={(e) => handleChange(e, "password")}
              />

              <button
                type="button"
                className="self-end mt-2 p-0 bg-transparent border-none text-sm text-[#0458f6] font-medium font-poppins cursor-pointer hover:underline"
                onClick={navigateForgotPassword}
              >
                Forgot password
              </button>

              <BlueButton
                id="loginButton"
                className="w-full mt-5 py-2.5 text-base font-semibold hover:text-white"
                onClick={() => handlerLogin()}
                isLoading={loginLoading}
              >
                Login
              </BlueButton>

              <p className="text-center mt-5 mb-0 text-xs leading-5 text-slate-500 font-poppins">
                By continuing you agree to our{' '}
                <Link className="text-[#0458f6] font-medium hover:underline" to="/legal/terms-of-use">
                  Terms of Service
                </Link>
                {' '}and{' '}
                <Link className="text-[#0458f6] font-medium hover:underline" to="/legal/privacy-policy">
                  Privacy Policy
                </Link>
              </p>

              <div className="auth-footer-rule" />

              <SupportBlock area="login" tailwindProps="mt-0" />
              <p className="text-center mt-3 mb-0 text-xs leading-5 text-slate-500 font-poppins">
                Want to remove your account?{' '}
                <a
                  className="text-[#0458f6] font-medium hover:underline"
                  href="mailto:support@tripplo.co?subject=Tripplo - Account Removal Request&body=Please supply your email address below. Account deletion requests can take up to 72Hrs to be processed. You will be contacted by a customer service agent to complete the deletion process.%0A%0A"
                >
                  Request account removal
                </a>
              </p>
            </div>
          </AuthForm>
    </AuthPageShell>
  )
}

export default Login
